import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import safeStorage from "../lib/safeStorage";
import { Calendar, MapPin, ShieldCheck, Star, Sparkles, ArrowRight } from "lucide-react";
import TrustBadges from "../components/TrustBadges";

/**
 * SharedExperience — public view for a tokenized share link.
 *
 * Visible to anyone with the token (the trusted contact or referral
 * recipient). Shows the booking + Concierge trust card. Never shows
 * customer PII beyond their first name.
 *
 * On render, the backend (/share/{token}) drops a 30-day `trel_share_token`
 * cookie so signup + booking conversions from this referrer are attributed
 * back to the share for Owner analytics.
 */
export default function SharedExperience() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/share/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Share link invalid or expired"));
    // Fallback when third-party cookies are blocked: keep the token in
    // localStorage so the axios api interceptor can attach it as a header.
    // 30-day TTL so the token doesn't sit in browsers forever — the share
    // record on the server also expires; this just guarantees the client
    // doesn't keep retransmitting a stale value.
    safeStorage.setWithExpiry("trel_share_token", token, 30 * 24 * 60 * 60 * 1000);
  }, [token]);

  if (err) {
    return (
      <div className="max-w-md mx-auto p-12 text-center">
        <h1 className="font-serif text-3xl">Share link unavailable</h1>
        <p className="text-[#071A2D]/65 mt-2">{err}</p>
        <Link to="/" className="btn-coral inline-block mt-6">Go to TREL</Link>
      </div>
    );
  }
  if (!data) return <div className="p-20 text-center text-[#071A2D]/60">Loading…</div>;

  const exp = data.experience || {};
  const c = data.concierge || {};
  const b = data.booking || {};
  const firstName = (c.name || "").split(" ")[0] || "this Concierge";
  const planPrompt = encodeURIComponent(
    `Plan a ${(exp.title || "luxury experience").toLowerCase()} like ${firstName} designed in ${exp.city || "the U.S."}.`
  );

  return (
    <div className="max-w-2xl mx-auto px-6 py-10" data-testid="shared-experience-page">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#064E3B] font-semibold">
        Shared on TREL
      </div>
      <h1 className="font-serif text-3xl md:text-4xl mt-2">
        {data.shared_by_first_name || "A friend"} shared their TREL experience
        {data.recipient_name ? ` with ${data.recipient_name}` : ""}.
      </h1>
      <p className="text-sm text-[#071A2D]/65 mt-2">
        Here&apos;s where they&apos;ll be and who&apos;s taking care of them.
      </p>

      <div className="mt-7 rounded-3xl overflow-hidden border border-[#071A2D]/10 shadow-luxury bg-[#FFF8EE]">
        {exp.image && <img src={exp.image} alt="" className="w-full h-56 object-cover" />}
        <div className="p-6">
          <h2 className="font-serif text-2xl">{exp.title || "Experience"}</h2>
          <div className="mt-3 text-sm text-[#071A2D]/70 flex items-center gap-3 flex-wrap">
            <span className="flex items-center gap-1.5"><Calendar size={14}/> {b.date} · {b.time}</span>
            <span className="flex items-center gap-1.5"><MapPin size={14}/> {exp.city}, {exp.state}</span>
            <span>· {b.guests} guests</span>
          </div>

          <div className="mt-6 p-4 rounded-2xl bg-white border border-[#071A2D]/10">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#064E3B] font-semibold">Concierge</div>
            <div className="mt-3 flex items-center gap-3">
              {c.photo && <img src={c.photo} alt="" className="w-14 h-14 rounded-full object-cover" />}
              <div className="flex-1">
                <div className="font-medium text-base">{c.name}</div>
                <div className="text-xs text-[#071A2D]/60 flex items-center gap-1 mt-0.5">
                  <Star size={12} fill="#C9A45C" color="#C9A45C"/> {Number(c.rating || 0).toFixed(1)}
                  {c.city ? <span className="ml-1">· {c.city}{c.state ? `, ${c.state}` : ""}</span> : null}
                </div>
              </div>
            </div>
            <div className="mt-3">
              <TrustBadges provider={c} size="sm" />
            </div>
            {Array.isArray(c.specializes) && c.specializes.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {c.specializes.slice(0, 4).map((s) => (
                  <span key={s} className="text-[11px] px-2 py-0.5 rounded-full bg-[#064E3B]/8 text-[#064E3B]">{s}</span>
                ))}
              </div>
            )}
          </div>

          {/* Referral CTA — turn the share view into a "Start Planning" funnel */}
          <div className="mt-6 rounded-2xl bg-[#064E3B] text-[#FFF8EE] p-5">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#C9A45C] font-semibold">
              <Sparkles size={12}/> Create your own
            </div>
            <div className="font-serif text-xl mt-2 leading-tight">
              Want your own experience with {firstName}?
            </div>
            <p className="text-xs text-[#FFF8EE]/80 mt-2 leading-relaxed">
              Tell TREL what you have in mind. Our AI Concierge will design it and {firstName} (or someone equally trusted) will bring it to life.
            </p>
            <Link
              to={`/ai?q=${planPrompt}`}
              data-testid="shared-start-planning-cta"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#C9A45C] text-[#071A2D] px-5 py-2.5 text-sm font-semibold hover:brightness-110 transition"
            >
              Start Planning <ArrowRight size={14}/>
            </Link>
          </div>

          <div className="mt-6 rounded-2xl bg-[#064E3B]/5 border border-[#064E3B]/15 p-4 text-xs text-[#071A2D]/75 leading-relaxed">
            <div className="flex items-center gap-2 font-semibold text-[#064E3B]">
              <ShieldCheck size={14}/> Privacy
            </div>
            <p className="mt-1.5">
              This page shows your Concierge&apos;s public profile only. No payment, address, or private details are exposed.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6 text-center text-xs text-[#071A2D]/55">
        Powered by <Link to="/" className="underline text-[#064E3B]">TREL</Link> — One request. One concierge. One experience.
      </div>
    </div>
  );
}
